/**
 * Auth store. Token + user persisted to localStorage so every tab shares
 * one session: opening a new tab, or a link in one, no longer asks the
 * operator to sign in again (it did while this lived in sessionStorage,
 * which is per tab). Tabs stay in step through the `storage` event: a
 * refreshed token, a sign-in or a sign-out in one tab reaches the others.
 * Sign-out clears it everywhere; the JWT itself still expires server-side.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthUser } from '@/api/auth'
import { hasPerm, hasAnyPerm, hasAllPerms, hasRole } from '@/lib/rbac'

interface AuthState {
  token: string | null
  user: AuthUser | null

  setSession: (token: string, user: AuthUser) => void
  setToken: (token: string) => void
  setUser: (user: AuthUser) => void
  logout: () => void

  hasPerm: (perm: string) => boolean
  hasAnyPerm: (perms: string[]) => boolean
  hasAllPerms: (perms: string[]) => boolean
  hasRole: (role: string) => boolean
  isAuthenticated: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,

      setSession: (token, user) => set({ token, user }),
      setToken: (token) => set({ token }),
      setUser: (user) => set({ user }),
      logout: () => set({ token: null, user: null }),

      hasPerm: (perm) => hasPerm(get().user?.perms ?? [], perm),
      hasAnyPerm: (perms) => hasAnyPerm(get().user?.perms ?? [], perms),
      hasAllPerms: (perms) => hasAllPerms(get().user?.perms ?? [], perms),
      hasRole: (role) => hasRole(get().user?.roles ?? [], role),
      isAuthenticated: () => !!get().token && !!get().user,
    }),
    {
      name: 'admindash-v2-auth', // AUTH_STORAGE_KEY
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ token: s.token, user: s.user }),
    },
  ),
)

export const AUTH_STORAGE_KEY = 'admindash-v2-auth'

/**
 * One-time carry-over: a tab signed in before this change holds its session
 * in sessionStorage. Move it to localStorage (unless one is already there) so
 * the operator isn't signed out by the upgrade itself.
 */
function migrateFromSessionStorage() {
  try {
    const old = window.sessionStorage.getItem(AUTH_STORAGE_KEY)
    if (old && !window.localStorage.getItem(AUTH_STORAGE_KEY)) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, old)
      void useAuthStore.persist.rehydrate()
    }
    if (old) window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
  } catch {
    /* storage unavailable: nothing to migrate */
  }
}

/** Keep every open tab on the same session. */
function syncAcrossTabs() {
  window.addEventListener('storage', (e) => {
    if (e.key === AUTH_STORAGE_KEY || e.key === null) void useAuthStore.persist.rehydrate()
  })
}

if (typeof window !== 'undefined') {
  migrateFromSessionStorage()
  syncAcrossTabs()
}
